import { Button, Drawer, Grid, makeStyles, Paper, TablePagination, Typography } from "@material-ui/core";
import {
  AddCircleRounded, Cancel, CheckCircleRounded, Edit, FilterList
} from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { useAuth } from "../../../context/auth";
import { useThemeList } from "../../../context/lists/themeList";
import { actionShowSpinner } from "../../../redux-flow/action/Wrapper/Spinner";
import { getAllThemes } from "../../../services/themes.service";
import { FREQUENCY } from "../../../utils/StaticLists";
import { Table } from "../../Common/Table";
import { checkAccess } from "../utils/index";
import FilterDrawer from './FilterDrawer';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "1vh 1vw",
    width: "100%"
  },
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    minWidth: 180,
    minHeight: 50,
    borderRadius: 25,
    marginTop: "5vh"
  },
  filterButton: {
    ...theme.typography.subtitle1,
    backgroundColor: '#fff',
    color: '#666666',
    borderRadius: 8,
    border: '1px solid #666666',
    textTransform: "uppercase",
    maxWidth: 140,
    width: '100%',
  },
  spacer: {
    marginBottom: 20
  },
  success: {
    backgroundColor: theme.palette.success.dark,
    borderRadius: 5,
    height: 50
  },
  label: {
    ...theme.typography.subtitle1,
    color: theme.palette.primary.contrastText
  },
  icon: {
    color: theme.palette.primary.contrastText,
    fontSize: 30,
    marginRight: 50
  }
}));

const ListThemes = ({ t, history, actionShowSpinner, location }) => {
  const classes = useStyles();
  const { userAccess } = useAuth();
  const { listOptions, updateFilterParams, updatePagination } = useThemeList();
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  const [successCreateThemeState, setSuccessCreateThemeState] = useState(location?.state?.successCreateTheme);
  const [successEditThemeState, setSuccessEditThemeState] = useState(location?.state?.successEditTheme);
  const [successDeleteThemeState, setSuccessDeleteThemeState] = useState(location?.state?.successDeleteTheme);
  
  const [themesList, setThemesList] = useState([]);
  
  const [totalThemes, setTotalThemes] = useState(0);

  const [canCreateTheme, setCanCreateTheme] = useState(false);
  const [canUpdateTheme, setCanUpdateTheme] = useState(false);

  useEffect(() => {
    if (typeof location?.state?.successCreateTheme === 'boolean' && location?.state?.successCreateTheme) {
      setTimeout(() => setSuccessCreateThemeState(false), 10000)
    }
    if (typeof location?.state?.successEditTheme === 'boolean' && location?.state?.successEditTheme) {
      setTimeout(() => setSuccessEditThemeState(false), 10000)
    }
    if (typeof location?.state?.successDeleteTheme === 'boolean' && location?.state?.successDeleteTheme) {
      setTimeout(() => setSuccessDeleteThemeState(false), 10000)
    }
  }, [location]);

  useEffect(() => {
    actionShowSpinner(true);

    if(userAccess){
      const hasAccess = checkAccess(userAccess, 'listThemes');
      const hasAccessToCreateTheme = checkAccess(userAccess, 'createTheme');
      const hasAccessToUpdateTheme = checkAccess(userAccess, 'updateTheme');

      setCanCreateTheme(hasAccessToCreateTheme);
      setCanUpdateTheme(hasAccessToUpdateTheme);

      if(!hasAccess){
        history.push('/');
      } 

      getAllThemes({
        ...(listOptions.filterParams.committee && {id: listOptions.filterParams.committee.id}),
        ...(listOptions.filterParams.keyword && {keyword: listOptions.filterParams.keyword}),
        currentPage: listOptions.pagination.page,
        pageSize: listOptions.pagination.size,
      }).then(res => {
        if(res.error.length > 1) {
          setThemesList([]);
          setTotalThemes(0);

          return;
        }
        const languageId = localStorage.getItem("i18nextLng");

        const formattedTheme = res.data.items.map(theme => ({
          ...theme,
          frequency: FREQUENCY[languageId].find(frequency => frequency.id === theme.frequencyId).name || '',
        }));

        setThemesList(formattedTheme);
        setTotalThemes(res.data.countItems);
      }).catch(() => setThemesList([])).finally(() => actionShowSpinner(false));
    }
  }, [userAccess, history, listOptions, actionShowSpinner]);

  const columns = [
    {
      title: t("forms.committeeName").toUpperCase(),
      field: "committeeName",
    },
    {
      title: t("forms.themeName").toUpperCase(),
      field: "themeName",
    },
    {
      title: t("forms.committee.frequency").toUpperCase(),
      field: "frequency",
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Drawer
        anchor="right"
        open={filterDrawerOpened}
        onClose={() => {
          setFilterDrawerOpened(false);
        }}
      >
        <FilterDrawer
          actionShowSpinner={(opened) => actionShowSpinner(opened)}
          onClose={() => {
            setFilterDrawerOpened(false);
          }}
          t={t}
          setFilters={(newValues) => {
            updateFilterParams(newValues);
            updatePagination({ ...listOptions.pagination, page: 0, });
          }}
          filters={listOptions.filterParams}
        />
      </Drawer>
      <Grid item className={classes.spacer}>
        <Typography variant="h4">
          {t("themes")}
        </Typography>
      </Grid>
      <Grid container item lg={12}>
        <Paper elevation={1} className={classes.root}>
          <Grid container item spacing={3}>
            <Grid container item direction="row" >
              <Button
                className={classes.filterButton}
                startIcon={<FilterList style={{ fontSize: 30 }} />}
                onClick={() => setFilterDrawerOpened(true)}
              >
                {t("forms.filters")}
              </Button>
            </Grid>
            
            <Grid container item direction="row" >
              <Grid item md={12} lg={12}>
                <Table
                  columns={columns}
                  data={themesList}
                  actions={[
                    {
                      icon: () => <Edit />,
                      tooltip: t("edit"),
                      disabled: !canUpdateTheme,
                      onClick: (_event, { id }) => {
                        history.push(`/theme/edit/${id}`);
                      },
                    },
                  ]}
                  pageSize={listOptions.pagination.size}
                  components={{
                    Pagination: props => {
                      return (
                        <TablePagination
                          style={{ float: "right" }}
                          rowsPerPageOptions={[5, 10, 20]}
                          rowsPerPage={listOptions.pagination.size}
                          labelRowsPerPage={t("table.labelRowsPerPage")}
                          page={listOptions.pagination.page}
                          labelDisplayedRows={(_data) => {
                            let numberOfPages = Math.ceil(totalThemes / listOptions.pagination.size);

                            if (numberOfPages <= 1) {
                              numberOfPages = 1;
                            }

                            return `${listOptions.pagination.page + 1} ${t("table.labelDisplayedRows")} ${numberOfPages}`
                          }}
                          onPageChange={(_e, page) => {
                            updatePagination({
                              page: page,
                              size: listOptions.pagination.size
                            });
                          }}
                          onRowsPerPageChange={event => {
                            const newSize = event.target.value;

                            updatePagination({
                              page: 0,
                              size: newSize,
                            });
  
                            props.onChangeRowsPerPage(event);
                          }}
                          nextIconButtonProps={{
                            disabled: listOptions.pagination.page + 1 >= Math.ceil(totalThemes / listOptions.pagination.size)
                          }}
                          backIconButtonProps={{
                            disabled: listOptions.pagination.page + 1 <= 1
                          }}
                        />
                      )
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Grid item lg={12}>
              {successCreateThemeState && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      <CheckCircleRounded className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("addSuccess").toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {successEditThemeState && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      <CheckCircleRounded className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("editSuccess").toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {successDeleteThemeState && (
                <Grid container item direction="row" alignItems="center" justify="center" className={classes.success}>
                  <Grid item>
                    {
                      <Cancel className={classes.icon} />
                    }
                  </Grid>
                  <Grid item>
                    <Typography className={classes.label}>
                      {t("deleteSuccess").toUpperCase()}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid container direction="row" justify="center" alignItems="center">
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            startIcon={<AddCircleRounded style={{ fontSize: 30 }} />}
            onClick={() => history.push("/theme/new")}
            disabled={!canCreateTheme}
          >
            {t("addThemeTable")}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const withRouterListThemes = withRouter(ListThemes);

const mapDispatchToProps = (dispatch) => ({
  actionShowSpinner: (request) => dispatch(actionShowSpinner(request)),
});

const ListThemesConnect = connect(
  null,
  mapDispatchToProps
)(withRouterListThemes);

export default withTranslation()(ListThemesConnect);